import { useState, useEffect, useCallback, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { heroSlides } from "@/mocks/homeData";
import LiveStatusPanel from "@/components/feature/LiveStatusPanel";

function ParticleCanvas() {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    let animId: number;
    const particles: { x: number; y: number; vx: number; vy: number; size: number; alpha: number }[] = [];

    const resize = () => {
      if (!mountedRef.current || !canvas) return;
      canvas.width = canvas.offsetWidth;
      canvas.height = canvas.offsetHeight;
    };
    resize();
    window.addEventListener("resize", resize);

    for (let i = 0; i < 40; i++) {
      particles.push({
        x: Math.random() * canvas.width,
        y: Math.random() * canvas.height,
        vx: (Math.random() - 0.5) * 0.4,
        vy: (Math.random() - 0.5) * 0.4,
        size: Math.random() * 2 + 0.5,
        alpha: Math.random() * 0.5 + 0.1,
      });
    }

    const animate = () => {
      if (!mountedRef.current) return;
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      particles.forEach((p) => {
        p.x += p.vx;
        p.y += p.vy;
        if (p.x < 0) p.x = canvas.width;
        if (p.x > canvas.width) p.x = 0;
        if (p.y < 0) p.y = canvas.height;
        if (p.y > canvas.height) p.y = 0;
        ctx.beginPath();
        ctx.arc(p.x, p.y, p.size, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(100, 220, 255, ${p.alpha})`;
        ctx.fill();
      });

      for (let i = 0; i < particles.length; i++) {
        for (let j = i + 1; j < particles.length; j++) {
          const dx = particles[i].x - particles[j].x;
          const dy = particles[i].y - particles[j].y;
          const dist = Math.sqrt(dx * dx + dy * dy);
          if (dist < 120) {
            ctx.beginPath();
            ctx.moveTo(particles[i].x, particles[i].y);
            ctx.lineTo(particles[j].x, particles[j].y);
            ctx.strokeStyle = `rgba(100, 220, 255, ${0.08 * (1 - dist / 120)})`;
            ctx.lineWidth = 0.5;
            ctx.stroke();
          }
        }
      }

      animId = requestAnimationFrame(animate);
    };
    animate();

    return () => {
      mountedRef.current = false;
      cancelAnimationFrame(animId);
      window.removeEventListener("resize", resize);
    };
  }, []);

  return <canvas ref={canvasRef} className="absolute inset-0 z-25 pointer-events-none" />;
}

export default function Hero({ onOpenQuote }: { onOpenQuote?: (service?: string) => void }) {
  const { t } = useTranslation("common");
  const navigate = useNavigate();
  const [current, setCurrent] = useState(0);
  const [previous, setPrevious] = useState<number | null>(null);
  const [crossfading, setCrossfading] = useState(false);
  const [videoLoaded, setVideoLoaded] = useState<boolean[]>(new Array(heroSlides.length).fill(false));
  const [allowVideo, setAllowVideo] = useState(false);
  // Non-critical hero chrome (particles, carousel arrows, dots, live panel) is
  // revealed only after the LCP image has painted, so it never competes with
  // the Largest Contentful Paint during the critical window.
  const [heroReady, setHeroReady] = useState(false);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const isAnimating = useRef(false);

  const totalSlides = heroSlides.length;

  // Smooth crossfade: keep both slides visible during transition
  const visibleSlides = [current];
  if (previous !== null && previous !== current) {
    visibleSlides.push(previous);
  }

  const goTo = useCallback(
    (index: number) => {
      if (isAnimating.current || index === current) return;
      isAnimating.current = true;

      // Start crossfade: previous stays visible, new fades in on top
      setPrevious(current);
      setCrossfading(true);
      setCurrent(index);

      // Finish crossfade after smooth overlap
      setTimeout(() => {
        setCrossfading(false);
        setPrevious(null);
        isAnimating.current = false;
      }, 1200);
    },
    [current],
  );

  const next = useCallback(() => {
    goTo((current + 1) % totalSlides);
  }, [current, totalSlides, goTo]);

  const prev = useCallback(() => {
    goTo((current - 1 + totalSlides) % totalSlides);
  }, [current, totalSlides, goTo]);

  useEffect(() => {
    intervalRef.current = setInterval(next, 8000);
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [next]);

  // Defer hero video download until the page has finished loading so it
  // does not compete with critical content (poster shows in the meantime).
  useEffect(() => {
    const enableVideo = () => setAllowVideo(true);
    if (document.readyState === "complete") {
      const timer = window.setTimeout(enableVideo, 1000);
      return () => window.clearTimeout(timer);
    }
    window.addEventListener("load", enableVideo, { once: true });
    const fallback = window.setTimeout(enableVideo, 4000);
    return () => {
      window.removeEventListener("load", enableVideo);
      window.clearTimeout(fallback);
    };
  }, []);

  // Safety net: reveal the hero controls even if the LCP image load event
  // never fires, so the carousel always stays usable.
  useEffect(() => {
    const reveal = window.setTimeout(() => setHeroReady(true), 2600);
    return () => window.clearTimeout(reveal);
  }, []);

  const resetInterval = () => {
    if (intervalRef.current) clearInterval(intervalRef.current);
    intervalRef.current = setInterval(next, 8000);
  };

  const handlePrev = () => {
    prev();
    resetInterval();
  };

  const handleNext = () => {
    next();
    resetInterval();
  };

  const handleDot = (index: number) => {
    if (index === current) return;
    goTo(index);
    resetInterval();
  };

  const handleNavClick = (href: string, service?: string) => {
    if (href === "#quote-modal") {
      onOpenQuote?.(service);
      return;
    }
    if (href.startsWith("/") && href !== "/") {
      navigate(href);
      return;
    }
    const el = document.querySelector(href);
    if (el) {
      el.scrollIntoView({ behavior: "smooth" });
    }
  };

  const slide = heroSlides[current];
  const isVideoSlide = (slide as any).videoOnly === true;

  const renderCtas = (s: typeof heroSlides[number], delayClass: string) => (
    <div className={`flex w-full sm:w-auto flex-col sm:flex-row items-center gap-3 md:gap-4 animate-fade-in-up ${delayClass}`}>
      <a
        href={s.ctaLink}
        onClick={(e) => {
          e.preventDefault();
          handleNavClick(s.ctaLink, (s as any).quoteService);
        }}
        className="group inline-flex w-full sm:w-auto items-center justify-between sm:justify-start gap-4 pl-6 pr-2 py-2 rounded-full border border-primary-400/50 bg-primary-500/10 backdrop-blur-md text-foreground-50 font-label font-medium text-sm md:text-base hover:border-primary-400/80 hover:bg-primary-500/20 transition-all duration-500 whitespace-nowrap cursor-pointer"
      >
        <span>{s.cta}</span>
        <span className="flex items-center justify-center w-9 h-9 md:w-10 md:h-10 rounded-full bg-primary-500 text-foreground-50 group-hover:bg-primary-400 transition-colors duration-300">
          {s.ctaLink === "#quote-modal" ? (
            <i className="ri-chat-smile-3-line text-base md:text-lg" />
          ) : (
            <i className="ri-arrow-right-line text-base md:text-lg group-hover:translate-x-0.5 transition-transform duration-300" />
          )}
        </span>
      </a>
      <a
        href={s.secondaryLink}
        onClick={(e) => {
          e.preventDefault();
          handleNavClick(s.secondaryLink, (s as any).quoteService);
        }}
        className="group inline-flex w-full sm:w-auto items-center justify-between sm:justify-start gap-4 pl-6 pr-2 py-2 rounded-full border border-foreground-100/15 bg-foreground-100/5 backdrop-blur-md text-foreground-100 font-label font-medium text-sm md:text-base hover:border-primary-400/40 hover:bg-foreground-100/10 transition-all duration-500 whitespace-nowrap cursor-pointer"
      >
        <span>{s.secondaryCta}</span>
        <span className="flex items-center justify-center w-9 h-9 md:w-10 md:h-10 rounded-full border border-primary-400/40 text-primary-300 group-hover:bg-primary-500 group-hover:text-foreground-50 group-hover:border-primary-500 transition-all duration-300">
          {s.secondaryLink === "#quote-modal" ? (
            <i className="ri-chat-smile-3-line text-base md:text-lg" />
          ) : (
            <i className="ri-arrow-right-line text-base md:text-lg group-hover:translate-x-0.5 transition-transform duration-300" />
          )}
        </span>
      </a>
    </div>
  );

  return (
    <section id="hero" className="relative w-full h-screen min-h-[650px] max-h-[1000px] overflow-hidden bg-background-950">
      {/* Video/Image slides — cinematic crossfade with Ken Burns zoom */}
      <div className="absolute inset-0">
        {visibleSlides.map((slideIdx) => {
          const s = heroSlides[slideIdx];
          const isExiting = slideIdx === previous && crossfading;
          const isEntering = slideIdx === current && crossfading;
          const isStatic = slideIdx === current && !crossfading;

          return (
            <div
              key={`slide-${s.id}`}
              className={`absolute inset-0 transition-all duration-[1200ms] ease-[cubic-bezier(0.4,0,0.2,1)] ${
                isStatic || isEntering
                  ? "opacity-100 z-10 scale-100"
                  : isExiting
                    ? "opacity-50 z-5 scale-[1.04]"
                    : "opacity-0 z-0 scale-[1.03]"
              }`}
            >
              {s.video ? (
                <>
                  {allowVideo && (
                  <video
                    autoPlay
                    loop
                    muted
                    playsInline
                    preload="none"
                    poster={s.image}
                    onLoadedData={() => {
                      setVideoLoaded((prev) => {
                        const next = [...prev];
                        next[slideIdx] = true;
                        return next;
                      });
                    }}
                    className={`w-full h-full object-cover object-center transition-opacity duration-1000 ${videoLoaded[slideIdx] ? "opacity-100" : "opacity-0"}`}
                    style={{ filter: "brightness(0.5) contrast(1.1)" }}
                  >
                    <source src={s.video} type="video/mp4" />
                  </video>
                  )}
                  <img
                    src={s.image}
                    alt={s.title}
                    width={1600}
                    height={833}
                    srcSet={s.imageSrcSet}
                    sizes="100vw"
                    loading={slideIdx === 0 ? "eager" : "lazy"}
                    className={`absolute inset-0 w-full h-full object-cover object-center transition-opacity duration-1000 ${videoLoaded[slideIdx] ? "opacity-0" : "opacity-100"}`}
                    style={{ filter: "brightness(0.5) contrast(1.1)" }}
                    decoding="async"
                    fetchPriority={slideIdx === 0 ? "high" : "low"}
                    onLoad={() => {
                      if (slideIdx === 0) setHeroReady(true);
                    }}
                  />
                </>
              ) : (
                <img
                  src={s.image}
                  alt={s.title}
                  width={1600}
                  height={833}
                  srcSet={s.imageSrcSet}
                  sizes="100vw"
                  loading={slideIdx === 0 ? "eager" : "lazy"}
                  className="w-full h-full object-cover object-center"
                  style={{ filter: "brightness(0.5) contrast(1.1)" }}
                  decoding="async"
                  fetchPriority={slideIdx === 0 ? "high" : "low"}
                  onLoad={() => {
                    if (slideIdx === 0) setHeroReady(true);
                  }}
                />
              )}
            </div>
          );
        })}
      </div>

      {/* Cinematic flash overlay - syncs with transition rhythm */}
      <div
        className={`absolute inset-0 z-15 pointer-events-none transition-opacity duration-300 ${
          crossfading ? "opacity-100" : "opacity-0"
        }`}
      >
        <div className="absolute inset-0 bg-gradient-to-b from-primary-400/12 via-primary-300/6 to-transparent animate-crossfade-flash" />
      </div>

      {/* Dark overlays */}
      <div className={`absolute inset-0 z-20 pointer-events-none transition-all duration-[1200ms] ease-[cubic-bezier(0.4,0,0.2,1)] ${isVideoSlide ? "bg-gradient-to-b from-background-950/35 via-background-950/15 to-background-950/60" : "bg-gradient-to-b from-background-950/70 via-background-950/30 to-background-950/85"}`} />
      <div className="absolute inset-0 z-20 pointer-events-none bg-gradient-to-r from-background-950/50 via-transparent to-background-950/50" />

      {/* Particle canvas (deferred until after the LCP image paints) */}
      {heroReady && <ParticleCanvas />}

      {/* Digital noise overlay */}
      <div className="absolute inset-0 z-20 pointer-events-none opacity-[0.03] mix-blend-overlay">
        <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSIzMDAiIGhlaWdodD0iMzAwIj48ZmlsdGVyIGlkPSJnoiI+PGZlVHVyYnVsZW5jZSB0eXBlPSJmcmFjdGFsTm9pc2UiIGJhc2VGcmVxdWVuY3k9IjAuNjUiIG51bU9jdGF2ZXM9IjMiIHN0aXRjaFRpbGVzPSJzdGl0Y2giLz48L2ZpbHRlcj48cmVjdCB3aWR0aD0iMTAwJSIgaGVpZ2h0PSIxMDAlIiBmaWx0ZXI9InVybCgjZykpIG9wYWNpdHk9IjAuNSIvPjwvc3ZnPg==')]" />
      </div>

      {/* Scan line effect */}
      <div className="absolute inset-0 z-20 pointer-events-none overflow-hidden opacity-[0.03]">
        <div
          className="absolute inset-0 bg-gradient-to-b from-transparent via-primary-300/40 to-transparent"
          style={{ animation: "scan-line 6s linear infinite" }}
        />
      </div>

      {/* Corner decorations */}
      <div className="absolute top-8 left-8 z-20 hidden lg:block">
        <div className="w-14 h-14 border-t border-l border-primary-400/15" />
      </div>
      <div className="absolute top-8 right-8 z-20 hidden lg:block">
        <div className="w-14 h-14 border-t border-r border-primary-400/15" />
      </div>
      <div className="absolute bottom-8 left-8 z-20 hidden lg:block">
        <div className="w-14 h-14 border-b border-l border-primary-400/15" />
      </div>
      <div className="absolute bottom-8 right-8 z-20 hidden lg:block">
        <div className="w-14 h-14 border-b border-r border-primary-400/15" />
      </div>

      {/* Slide number indicator */}
      <div className="absolute top-8 right-20 z-20 hidden lg:block">
        <span className="font-label text-xs text-foreground-400/30 tracking-widest">
          {String(current + 1).padStart(2, "0")} / {String(totalSlides).padStart(2, "0")}
        </span>
      </div>

      {/* Content */}
      <div className="relative z-30 w-full h-full flex flex-col items-center justify-center px-4 sm:px-6 lg:px-10">
        {/* Slide content — re-mounts on every slide change to auto-trigger entrance animations */}
        <div key={`slide-content-${current}`} className="flex flex-col items-center">
          {isVideoSlide ? (
            <>
              <p className="text-sm md:text-base text-foreground-200/70 text-center max-w-lg mb-8 md:mb-10 animate-fade-in-up delay-100">
                {t("hero.videoDesc")}
              </p>
              {renderCtas(slide, "delay-200")}
            </>
          ) : (
            <>
              {/* Main title */}
              <h1 className="font-heading font-bold text-3xl sm:text-4xl md:text-5xl lg:text-6xl xl:text-7xl text-center text-foreground-50 leading-tight mb-4 md:mb-6 max-w-4xl animate-fade-in-up delay-100">
                {slide.title}{" "}
                <span className="bg-gradient-to-r from-primary-300 via-primary-400 to-accent-400 bg-clip-text text-transparent glow-text">
                  {slide.titleHighlight}
                </span>
              </h1>

              {/* Subtitle */}
              <p className="text-sm sm:text-base md:text-lg text-foreground-300/75 text-center max-w-2xl mb-8 md:mb-10 leading-relaxed animate-fade-in-up delay-200">
                {slide.subtitle}
              </p>

              {/* CTAs */}
              {renderCtas(slide, "delay-300")}
            </>
          )}
        </div>
      </div>

      {/* Live status panel (unifies the CyberSOC concept on desktop) */}
      {heroReady && (
        <div className="hidden lg:block absolute right-5 xl:right-10 bottom-20 z-30 w-[19rem]">
          <LiveStatusPanel />
        </div>
      )}

      {/* Arrow navigation */}
      <button
        type="button"
        onClick={handlePrev}
        className={`absolute left-4 md:left-8 top-1/2 -translate-y-1/2 z-30 w-10 h-10 md:w-12 md:h-12 flex items-center justify-center rounded-full border border-foreground-100/8 bg-background-900/25 backdrop-blur-md text-foreground-100/50 hover:text-foreground-100 hover:border-primary-400/25 hover:bg-background-900/45 transition-all duration-300 cursor-pointer group ${heroReady ? "opacity-100" : "opacity-0 pointer-events-none"}`}
        aria-label="Slide anterior"
      >
        <i className="ri-arrow-left-line text-lg md:text-xl group-hover:-translate-x-0.5 transition-transform duration-300" />
      </button>
      <button
        type="button"
        onClick={handleNext}
        className={`absolute right-4 md:right-8 top-1/2 -translate-y-1/2 z-30 w-10 h-10 md:w-12 md:h-12 flex items-center justify-center rounded-full border border-foreground-100/8 bg-background-900/25 backdrop-blur-md text-foreground-100/50 hover:text-foreground-100 hover:border-primary-400/25 hover:bg-background-900/45 transition-all duration-300 cursor-pointer group ${heroReady ? "opacity-100" : "opacity-0 pointer-events-none"}`}
        aria-label="Slide siguiente"
      >
        <i className="ri-arrow-right-line text-lg md:text-xl group-hover:translate-x-0.5 transition-transform duration-300" />
      </button>

      {/* Dots */}
      <div className={`absolute bottom-8 md:bottom-10 left-1/2 -translate-x-1/2 z-30 flex items-center gap-2 md:gap-3 transition-opacity duration-700 ${heroReady ? "opacity-100" : "opacity-0 pointer-events-none"}`}>
        {heroSlides.map((s, i) => {
          const isVideoDot = (s as any).videoOnly === true;
          return (
            <button
              key={s.id}
              type="button"
              onClick={() => handleDot(i)}
              className={`rounded-full transition-all duration-500 cursor-pointer ${
                i === current
                  ? isVideoDot
                    ? "w-6 md:w-8 h-2 bg-gradient-to-r from-red-400 to-red-500 animate-pulse"
                    : "w-6 md:w-8 h-2 bg-gradient-to-r from-primary-400 to-accent-400"
                  : isVideoDot
                    ? "w-2 h-2 bg-red-400/25 hover:bg-red-400/50 flex items-center justify-center"
                    : "w-2 h-2 bg-foreground-100/15 hover:bg-foreground-100/30"
              }`}
              aria-label={isVideoDot ? "Slide de video" : `Ir al slide ${i + 1}`}
            >
              {!isVideoDot ? null : i !== current ? (
                <i className="ri-play-circle-fill text-red-400/40 text-[10px] leading-none" />
              ) : null}
            </button>
          );
        })}
      </div>

      {/* Progress bar */}
      <div className={`absolute bottom-0 left-0 right-0 z-30 h-0.5 bg-foreground-100/5 transition-opacity duration-700 ${heroReady ? "opacity-100" : "opacity-0"}`}>
        <div
          key={current}
          className={`h-full ${isVideoSlide ? "bg-gradient-to-r from-red-400 via-red-500 to-red-600" : "bg-gradient-to-r from-primary-400 via-primary-500 to-accent-400"}`}
          style={{ animation: "slide-progress 8s linear forwards" }}
        />
      </div>

      {/* Scroll indicator */}
      <div className="absolute bottom-4 left-1/2 -translate-x-1/2 z-30 flex flex-col items-center gap-1.5 opacity-40 hover:opacity-70 transition-opacity duration-500 cursor-pointer"
        onClick={() => handleNavClick("#threat-map")}
      >
        <span className="text-[9px] font-label text-foreground-400 uppercase tracking-widest">{t("hero.scroll")}</span>
        <div className="w-4 h-6 rounded-full border border-foreground-400/30 flex items-start justify-center p-1">
          <div className="w-1 h-1.5 rounded-full bg-primary-300 animate-bounce" style={{ animationDuration: "1.5s" }} />
        </div>
      </div>

      <style>{`
        @keyframes slide-progress {
          from { width: 0%; }
          to { width: 100%; }
        }
        @keyframes scan-line {
          0% { transform: translateY(-100%); }
          100% { transform: translateY(100%); }
        }
      `}</style>
    </section>
  );
}